import * as Sentry from '@sentry/react'
import isEqual from 'lodash/isEqual'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useQueryClient } from 'react-query'
import { useEffectOnce } from 'react-use'
import store from 'store'

import agent from 'agent'
import {
  type AuthContextValues,
  DEFAULT_AUTH_VALUES,
  getEntitlementsForAuth,
  getOpenApi,
  getPrivilegeForEndpoint,
  getRolesFromLocalStorage,
  getSessionFromLocalStorage,
  getUserSession,
  isPersistedSessionHeader,
  renewUserSession,
  resolveCurrentTeamId,
  setActiveTeamIdInLocalStorage,
  TEAM_DETAILS_FALLBACK
} from 'context/auth-context-utils'
import toast from 'lib/toast'
import { useGetRoles } from 'queries/roles/use-get-roles'
import { resetAnalytics } from 'track'
import { trimOpenApiFile } from 'utils/openapi-utils'
import openApiJson from 'utils/openapi.json'
import { NO_TEAM } from 'utils/session'
import { ms } from 'utils/time/millisecond'

import type { OpenApiResponse } from 'types/api/openapi'
import type { RbacBody, RbacPrivilege } from 'types/api/rbac/rbac'
import type {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'
import type { ApiProp } from 'utils/endpoints-access'

export { DEFAULT_AUTH_VALUES, NO_TEAM }
export const AuthContext = createContext(DEFAULT_AUTH_VALUES)

const SESSION_POLLING_TIME = ms.minutes(10)
const OPENAPI_POLLING_TIME = ms.hours(1)

export const AuthProvider: React.FC = ({ children }) => {
  // TODO: I am pretty sure that our session management implementation deletes this storage
  // key any time the session ends, so this will always be undefined until that is fixed.
  const cachedRoles = useRef(getRolesFromLocalStorage())
  const [
    { ready, selectedTeamId, session, isRbacEnabled, openApi, RBAC },
    setAuthState
  ] = useState(() => ({
    isRbacEnabled: false,
    openApi: openApiJson as unknown as OpenApiResponse,
    RBAC: cachedRoles.current ?? ({} as RbacBody),
    ready: false,
    selectedTeamId: '',
    session: getSessionFromLocalStorage()
  }))
  const currentTeamId = resolveCurrentTeamId(session, selectedTeamId)

  const queryClient = useQueryClient()

  /*
   * Copies the `localStorage` session every tab shares into this tab's state. Call it after this
   * tab writes the session, and on each `storage` event from another tab.
   */
  const syncSessionFromLocalStorage = useCallback(() => {
    setAuthState((state) => {
      const persistedSession = getSessionFromLocalStorage()

      // Returns the current state while the sessions match, so React skips the render.
      if (isEqual(persistedSession, state.session)) return state

      return { ...state, session: persistedSession }
    })
  }, [])

  const setOpenApi = useCallback(() => {
    // returns openApiFallback
    return getOpenApi({
      onSuccess: (openApi) => {
        setAuthState((current) => ({
          ...current,
          openApi: trimOpenApiFile(openApi)
        }))
      }
    })
  }, [])

  const endSession = useCallback(async () => {
    // While a session is saved, this tab clears `localStorage` and logs it out on the server.
    if (store.get('session')) {
      /*
       * The following action wipes out all local storage persistence without discretion, whereas the
       * need to persist the user preference state for alert visibility is needed. This single key
       * is temporarily stored and re-applied following the clear() call.
       */
      const alertPreferencesPersistence = store.get('alertPreferences')
      const redirectPathPersistence = store.get('loginRedirect')
      const lastActiveOrgId = store.get('lastActiveOrgId')
      const rbacSummary = store.get('rbac-summary')

      /*
       * We store some user settings in localStorage to survive page navigation
       * so make sure that's cleared out on logout
       */
      window.localStorage.clear()
      // re-applying the user alert preference object
      store.set('alertPreferences', alertPreferencesPersistence)
      store.set('loginRedirect', redirectPathPersistence)
      // persist last team id
      store.set('lastActiveOrgId', lastActiveOrgId)
      store.set('rbac-summary', rbacSummary)

      // Clearing first makes an `endSession` call from this request's error find no session.
      try {
        await agent.delete('/users/auth')
      } catch (err) {
        /* We want to wipe out all local storage and log the user out despite the success/failure of the agent.delete('/users/auth') call */
      }
    }

    syncSessionFromLocalStorage()
  }, [syncSessionFromLocalStorage])

  const fetchSession = useCallback(async () => {
    const persistedSession: UserSession | undefined = store.get('session')

    if (persistedSession) {
      const isExpired = new Date(persistedSession.expires_at) <= new Date()
      try {
        if (isExpired) {
          await renewUserSession(persistedSession)
        } else {
          const serverSession = await getUserSession(persistedSession)

          if (isPersistedSessionHeader(persistedSession.header))
            store.set('session', serverSession)
        }
      } catch (e) {
        if (isPersistedSessionHeader(persistedSession.header))
          return endSession()
      }
    }

    syncSessionFromLocalStorage()
  }, [endSession, syncSessionFromLocalStorage])

  const startSession = useCallback(
    async (newSession: UserSession) => {
      // `fetchSession` reads the session from `localStorage`, so the login writes it there first.
      store.set('session', newSession)
      return fetchSession()
    },
    [fetchSession]
  )

  const renewSession = useCallback(async () => {
    try {
      /*
       * When another tab renews first, `renewUserSession` skips its request. The re-render after it
       * reads the session the other tab saved, putting both tabs on the same renew token.
       */
      await renewUserSession(session)
    } catch (err) {
      return endSession()
    }

    syncSessionFromLocalStorage()
  }, [endSession, syncSessionFromLocalStorage, session])

  const setActiveTeamId = useCallback(
    async (teamId: string) => {
      setAuthState((current) => ({
        ...current,
        selectedTeamId: teamId
      }))

      setActiveTeamIdInLocalStorage(teamId, session.company_id)
    },
    [session.company_id]
  )

  /**
   * Get the session details for the user's team given a teamId
   */
  const getTeamDetails = useCallback(
    (teamId: string) => {
      return (session.teams.find((t) => t.identifier === teamId) ||
        TEAM_DETAILS_FALLBACK) as UserSessionTeam
    },
    [session.teams]
  )

  /**
   * Checks if the given privilege is in the company-wide privileges on a user's session
   */
  const hasCompanyPrivilege = useCallback(
    (privilege: RbacPrivilege) => session.privileges.includes(privilege),
    [session.privileges]
  )

  /**
   * Get the privileges for a user's team session given a teamId
   */
  const privilegesForTeam = useCallback(
    (teamId: string) => {
      return getTeamDetails(teamId).privileges
    },
    [getTeamDetails]
  )

  /**
   * Checks if the given privilege is in the privileges on a user's specified team session.
   * If not, checks hasCompanyPrivilege
   */
  const hasTeamPrivilege = useCallback(
    (teamId: string, privilege: RbacPrivilege) =>
      privilegesForTeam(teamId).includes(privilege) ||
      // Some high level roles can manage teams even if not a member
      hasCompanyPrivilege(privilege),
    [hasCompanyPrivilege, privilegesForTeam]
  )

  /**
   * Given an endpoint and method, checks if the user has access.
   * This is done by checking the user's session for the privilege associated with the endpoint.
   * More details in docs/rbac.md
   *
   * The `teamId` is optional. If it's included, it checks `hasTeamPrivilege`. Otherwise,
   * it checks `hasCompanyPrivilege`. (See the doc-strings on those functions for more details)
   */
  const hasEndpointAccess = useCallback(
    (
      apiProperties: ApiProp,
      options: {
        teamId?: string
      } = {}
    ) => {
      const { teamId } = options

      const privilegeForEndpoint = getPrivilegeForEndpoint(
        apiProperties.endpoint,
        apiProperties.method,
        openApi
      )

      if (!privilegeForEndpoint) {
        return false
      }

      if (teamId) {
        return hasTeamPrivilege(teamId, privilegeForEndpoint)
      }

      return hasCompanyPrivilege(privilegeForEndpoint)
    },
    [openApi, hasCompanyPrivilege, hasTeamPrivilege]
  )

  const hasEndpointAccessForAll = useCallback(
    (properties: ApiProp[], useTeamScope) =>
      properties.every((prop) =>
        hasEndpointAccess(prop, useTeamScope ? { teamId: currentTeamId } : {})
      ),
    [currentTeamId, hasEndpointAccess]
  )

  const authContextValue = useMemo<AuthContextValues>(() => {
    const currentTeam = getTeamDetails(currentTeamId)
    const currentRoles = session.roles.concat(currentTeam.roles)
    const isRunningInCypress = Boolean(window.Cypress)

    return {
      ...getEntitlementsForAuth(session),
      RBAC,
      authHeader: session.header,
      companyDisplayName: session.company_alias ?? session.company_name,
      companyId: session.company_id,
      companyIsAlfiEnabled: isRunningInCypress
        ? session.company_is_alfi_enabled
        : DEFAULT_AUTH_VALUES.companyIsAlfiEnabled, // todo: EN-5114 remove
      companyPrefs: session.prefs,
      companyPrivileges: session.privileges,
      currentTeam,
      currentTeamId,
      currentTeamRoles: currentTeam.roles,
      endSession,
      fetchSession,
      getTeamDetails,
      hasEndpointAccess,
      hasEndpointAccessForAll,
      isProductionEnvironment:
        session?.prefs?.production || currentTeam?.production,
      isRbacEnabled,
      privilegesForTeam,
      ready,
      renewSession,
      roles: currentRoles,
      session,
      setActiveTeamId,
      startSession,
      teams: session.teams
    }
  }, [
    RBAC,
    currentTeamId,
    isRbacEnabled,
    ready,
    session,
    endSession,
    fetchSession,
    getTeamDetails,
    hasEndpointAccess,
    hasEndpointAccessForAll,
    privilegesForTeam,
    renewSession,
    setActiveTeamId,
    startSession
  ])

  useEffectOnce(() => {
    fetchSession().then(() =>
      setAuthState((state) => ({ ...state, ready: true }))
    )
  })

  // Runs once per signed-in account. Its cleanup runs when this tab logs out or switches accounts.
  useEffect(() => {
    if (!session.identifier) return

    // `setOpenApi` returns the fallback spec at once and replaces it when the account's spec loads.
    setAuthState((state) => ({
      ...state,
      isRbacEnabled: false,
      openApi: setOpenApi(),
      // Pins this tab's team at sign-in, so a team switch in another tab leaves it in place.
      selectedTeamId: resolveCurrentTeamId(state.session, '')
    }))

    return () => {
      /*
       * `removeQueries` also cancels each in-flight fetch. Every response the cache receives next
       * belongs to the account this tab shows after the switch.
       */
      queryClient.removeQueries()
      /*
       * clear segment.com analytics data
       * TODO: Segment is no longer used to track Gremlin analytics.
       */
      resetAnalytics()

      // Removes this account's user from Sentry, so events after logout carry no user.
      Sentry.setUser(null)
    }
  }, [queryClient, session.company_id, session.identifier, setOpenApi])

  /*
   * Tags Sentry errors with this tab's user and team. A team switch reruns this effect, while the
   * per-account effect and its query cleanup run on account changes.
   */
  useEffect(() => {
    if (!session.identifier) return

    Sentry.setUser({ email: session.identifier, id: session.identifier })
    Sentry.setContext('session', {
      teamId: currentTeamId,
      companyId: session.company_id
    })
  }, [currentTeamId, session.company_id, session.identifier])

  // we need to update the session on a regular basis
  // as the restricted time windows can be updated by another user at any time
  // so we want to be sure that we get those updates quickly to display the warnings
  useEffect(() => {
    const sessionInterval = setInterval(fetchSession, SESSION_POLLING_TIME)

    return () => clearInterval(sessionInterval)
  }, [fetchSession])

  /*
   * Applies another tab's login, renewal, or logout to this tab's `session`. A new identifier or
   * company then reruns the per-account effect above, which clears the previous account's cache.
   */
  useEffect(() => {
    // After one tab writes `localStorage`, the browser fires a `storage` event in each other tab.
    const rerenderOnSessionStorageEvent = (event: StorageEvent) => {
      // A `null` key means another tab cleared all of `localStorage`, as `endSession` does.
      if (event.key === 'session' || event.key === null)
        syncSessionFromLocalStorage()
    }

    window.addEventListener('storage', rerenderOnSessionStorageEvent)

    return () =>
      window.removeEventListener('storage', rerenderOnSessionStorageEvent)
  }, [syncSessionFromLocalStorage])

  // for security reasons, we want to refresh openapi.json at a regular interval
  const openApiInterval = useRef<ReturnType<typeof setInterval>>()
  useEffect(() => {
    if (!openApiInterval.current) {
      openApiInterval.current = setInterval(() => {
        setOpenApi()
      }, OPENAPI_POLLING_TIME)
    }

    return () => {
      clearInterval(openApiInterval.current)
    }
  }, [setOpenApi])

  /*
   * Handle Roles fetching for the current user. First attempt to fetch roles from our endpoints,
   * and if that fails, attempt to pull data from localStorage. When both scenarios fail, we
   * should log out the user and display an error toast to inform them of the failure, as the
   * webapp cannot reliably function without this data.
   */
  useGetRoles({
    enabled: ready && Boolean(session.identifier),
    onSuccess(data) {
      setAuthState((state) => ({ ...state, RBAC: data, isRbacEnabled: true }))
      // Save a local copy of RbacBody to localStorage
      store.set('rbac-summary', { date: new Date(), summary: data })
    },
    onError() {
      const { current: cachedRbacSummary } = cachedRoles

      if (cachedRbacSummary) {
        setAuthState((state) => ({
          ...state,
          RBAC: cachedRbacSummary,
          isRbacEnabled: true
        }))
        return
      }

      endSession()
      toast({
        type: 'error',
        text: 'There was an issue getting a list of your roles, try logging in again.'
      })
    }
  })

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}
